import React, { useState, useEffect } from 'react'
import {
  Eye,
  Trash2,
  Download,
  FileEdit,
  Ban,
  AlertCircle,
  CheckCircle,
  Clock,
  Shield,
  Mail,
  User,
  Building,
  Calendar,
  ChevronDown,
  ChevronUp,
  Loader
} from 'lucide-react'

interface PrivacyRequest {
  id: string
  requestType: 'access' | 'delete' | 'portability' | 'rectification' | 'restriction' | 'objection' | 'withdraw-consent'
  email: string
  name: string
  company: string
  description: string
  verificationMethod: 'email' | 'id-document'
  status: 'pending-verification' | 'verified' | 'in-progress' | 'completed' | 'rejected'
  verificationCode: string
  verificationExpiry: number
  createdAt: string
  verifiedAt?: string
  completedAt?: string
  ipAddress?: string
  userAgent?: string
  adminNotes?: string
}

export default function PrivacyRequestsManager() {
  const [requests, setRequests] = useState<PrivacyRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedRequest, setExpandedRequest] = useState<string | null>(null)
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterType, setFilterType] = useState<string>('all')
  const [processingRequest, setProcessingRequest] = useState<string | null>(null)

  useEffect(() => {
    fetchRequests()
  }, [])

  const fetchRequests = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/admin-privacy-requests', {
        credentials: 'include',
        headers
      })

      if (response.status === 401) {
        // Authentication error - fail gracefully with empty state
        setRequests([])
        setError('Authentication required. Please ensure you are logged in with admin privileges.')
        return
      }

      if (!response.ok) {
        throw new Error('Failed to fetch privacy requests')
      }

      const data = await response.json()
      setRequests(data)
    } catch (err) {
      console.error('Error fetching privacy requests:', err)
      setError('Failed to load privacy requests. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleStatusUpdate = async (requestId: string, action: string, notes?: string) => {
    setProcessingRequest(requestId)
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/admin-privacy-requests', {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ requestId, action, notes })
      })

      if (!response.ok) {
        throw new Error('Failed to update request')
      }

      const result = await response.json()

      // Update local state
      setRequests(prev => prev.map(req =>
        req.id === requestId ? result.request : req
      ))

      alert(`Request ${action === 'complete' ? 'completed' : action === 'reject' ? 'rejected' : 'updated'} successfully`)
    } catch (err) {
      console.error('Error updating request:', err)
      alert('Failed to update request. Please try again.')
    } finally {
      setProcessingRequest(null)
    }
  }

  const getStatusBadge = (status: string) => {
    const styles = {
      'pending-verification': 'bg-yellow-100 text-yellow-800',
      'verified': 'bg-blue-100 text-blue-800',
      'in-progress': 'bg-purple-100 text-purple-800',
      'completed': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800'
    }

    const icons = {
      'pending-verification': Clock,
      'verified': CheckCircle,
      'in-progress': AlertCircle,
      'completed': CheckCircle,
      'rejected': Ban
    }

    const Icon = icons[status as keyof typeof icons]

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status as keyof typeof styles]}`}>
        <Icon className="h-3 w-3" />
        {status.replace('-', ' ')}
      </span>
    )
  }

  const getRequestTypeInfo = (type: string) => {
    const types = {
      'access': { icon: Eye, label: 'Access Request', color: 'text-blue-600' },
      'delete': { icon: Trash2, label: 'Deletion Request', color: 'text-red-600' },
      'portability': { icon: Download, label: 'Data Portability', color: 'text-green-600' },
      'rectification': { icon: FileEdit, label: 'Rectification', color: 'text-yellow-600' },
      'restriction': { icon: Ban, label: 'Processing Restriction', color: 'text-orange-600' },
      'objection': { icon: AlertCircle, label: 'Objection', color: 'text-purple-600' },
      'withdraw-consent': { icon: Shield, label: 'Withdraw Consent', color: 'text-pink-600' }
    }

    return types[type as keyof typeof types] || types.access
  }

  const filteredRequests = requests.filter(req => {
    if (filterStatus !== 'all' && req.status !== filterStatus) return false
    if (filterType !== 'all' && req.requestType !== filterType) return false
    return true
  })

  const stats = {
    total: requests.length,
    pendingVerification: requests.filter(r => r.status === 'pending-verification').length,
    verified: requests.filter(r => r.status === 'verified').length,
    inProgress: requests.filter(r => r.status === 'in-progress').length,
    completed: requests.filter(r => r.status === 'completed').length,
    rejected: requests.filter(r => r.status === 'rejected').length
  }

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
        <p className="text-sm text-gray-500">Loading privacy requests...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-12 text-center">
        <AlertCircle className="h-8 w-8 mx-auto mb-4 text-red-600" />
        <p className="text-sm text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchRequests}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div>
      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 p-6 border-b border-gray-200">
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-xs text-gray-600">Total</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-yellow-600">{stats.pendingVerification}</p>
          <p className="text-xs text-gray-600">Pending</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-blue-600">{stats.verified}</p>
          <p className="text-xs text-gray-600">Verified</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-purple-600">{stats.inProgress}</p>
          <p className="text-xs text-gray-600">In Progress</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
          <p className="text-xs text-gray-600">Completed</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
          <p className="text-xs text-gray-600">Rejected</p>
        </div>
      </div>

      {/* Filters */}
      <div className="p-6 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="pending-verification">Pending Verification</option>
              <option value="verified">Verified</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="all">All Types</option>
              <option value="access">Access Request</option>
              <option value="delete">Deletion Request</option>
              <option value="portability">Data Portability</option>
              <option value="rectification">Rectification</option>
              <option value="restriction">Processing Restriction</option>
              <option value="objection">Objection</option>
              <option value="withdraw-consent">Withdraw Consent</option>
            </select>
          </div>
        </div>
      </div>

      {/* Requests List */}
      <div className="divide-y divide-gray-200">
        {filteredRequests.length === 0 ? (
          <div className="p-12 text-center">
            <Shield className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-sm text-gray-500">No privacy requests found</p>
          </div>
        ) : (
          filteredRequests.map((request) => {
            const typeInfo = getRequestTypeInfo(request.requestType)
            const TypeIcon = typeInfo.icon
            const isExpanded = expandedRequest === request.id
            const isProcessing = processingRequest === request.id

            return (
              <div key={request.id} className="p-6 hover:bg-gray-50 transition-colors">
                {/* Request Summary */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <TypeIcon className={`h-5 w-5 ${typeInfo.color}`} />
                      <h3 className="text-sm font-semibold text-gray-900">{typeInfo.label}</h3>
                      {getStatusBadge(request.status)}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span>{request.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span>{request.email}</span>
                      </div>
                      {request.company && (
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-gray-400" />
                          <span>{request.company}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span>{new Date(request.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => setExpandedRequest(isExpanded ? null : request.id)}
                    className="ml-4 p-2 text-gray-400 hover:text-gray-600"
                  >
                    {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </button>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs font-medium text-gray-700 mb-1">Request ID</p>
                        <p className="text-sm text-gray-900 font-mono">{request.id}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-700 mb-1">Verification Method</p>
                        <p className="text-sm text-gray-900">{request.verificationMethod}</p>
                      </div>
                      {request.verifiedAt && (
                        <div>
                          <p className="text-xs font-medium text-gray-700 mb-1">Verified At</p>
                          <p className="text-sm text-gray-900">{new Date(request.verifiedAt).toLocaleString()}</p>
                        </div>
                      )}
                      {request.completedAt && (
                        <div>
                          <p className="text-xs font-medium text-gray-700 mb-1">Completed At</p>
                          <p className="text-sm text-gray-900">{new Date(request.completedAt).toLocaleString()}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs font-medium text-gray-700 mb-1">IP Address</p>
                        <p className="text-sm text-gray-900 font-mono">{request.ipAddress || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-700 mb-1">User Agent</p>
                        <p className="text-sm text-gray-900 truncate" title={request.userAgent}>
                          {request.userAgent || 'N/A'}
                        </p>
                      </div>
                    </div>

                    {request.description && (
                      <div className="mb-4">
                        <p className="text-xs font-medium text-gray-700 mb-1">Description</p>
                        <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">{request.description}</p>
                      </div>
                    )}

                    {request.adminNotes && (
                      <div className="mb-4">
                        <p className="text-xs font-medium text-gray-700 mb-1">Admin Notes</p>
                        <p className="text-sm text-gray-900 bg-blue-50 p-3 rounded-md">{request.adminNotes}</p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
                      {request.status === 'verified' && (
                        <button
                          onClick={() => handleStatusUpdate(request.id, 'mark-in-progress')}
                          disabled={isProcessing}
                          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 text-sm flex items-center gap-2"
                        >
                          {isProcessing ? <Loader className="h-4 w-4 animate-spin" /> : <AlertCircle className="h-4 w-4" />}
                          Mark In Progress
                        </button>
                      )}

                      {(request.status === 'verified' || request.status === 'in-progress') && (
                        <>
                          <button
                            onClick={() => {
                              const notes = prompt('Enter completion notes (optional):')
                              handleStatusUpdate(request.id, 'complete', notes || undefined)
                            }}
                            disabled={isProcessing}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm flex items-center gap-2"
                          >
                            {isProcessing ? <Loader className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                            Mark Complete
                          </button>
                          <button
                            onClick={() => {
                              const notes = prompt('Enter rejection reason:')
                              if (notes) {
                                handleStatusUpdate(request.id, 'reject', notes)
                              }
                            }}
                            disabled={isProcessing}
                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 text-sm flex items-center gap-2"
                          >
                            {isProcessing ? <Loader className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
                            Reject
                          </button>
                        </>
                      )}

                      {request.status === 'pending-verification' && (
                        <div className="text-sm text-yellow-600 flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Waiting for email verification
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
